import { productCatalog } from "@holocron/shared";
import type { CSSProperties } from "react";
import { Icon } from "../components/common/Icon";
import { ProductScreenshotShowcase } from "../components/common/ProductScreenshotShowcase";
import { SmartLink } from "../components/common/SmartLink";
import { HeroHolocronAnimation } from "../components/home/HeroHolocronAnimation";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const heroSignals = [
  "Secure AI implementation",
  "CMMC environments",
  "Evidence operations",
  "Machine-speed response",
  "Executive governance"
];

const assuranceSignals = [
  "Defense-grade workflows",
  "Policy-bound automation",
  "Audit-ready evidence",
  "Executive control plane"
];

const proofMetrics = [
  {
    value: "5",
    label: "Holocron mission lanes",
    copy: "AI implementation, regulated environments, evidence, authority, and advanced automation."
  },
  {
    value: "3",
    label: "Operating layers",
    copy: "Governance, infrastructure, and proof stay connected instead of drifting into separate workstreams."
  },
  {
    value: "30",
    label: "Day readiness sprint",
    copy: "A practical planning window for scoping AI, CMMC, evidence, and delivery priorities."
  },
  {
    value: "1",
    label: "Authority model",
    copy: "Humans define doctrine. Systems enforce boundaries, preserve context, and surface decisions."
  }
];

const industries = [
  "Defense industrial base",
  "Government-adjacent suppliers",
  "Critical infrastructure",
  "Regulated AI teams",
  "Healthcare and finance"
];

const productIcons = {
  "secure-ai": "brain",
  "rapid-factory": "shield",
  "kyber-docs": "document",
  odan: "omni",
  mawforge: "orbit"
} as const;

const platformLayers = productCatalog.map((product) => ({
  ...product,
  icon: productIcons[product.id]
}));

const workflowSignals = [
  "AI policy enforcement",
  "Contract intelligence",
  "CMMC environment buildout",
  "Evidence assembly",
  "Endpoint remediation",
  "Audit trail review",
  "Risk signal triage",
  "Agent workflow governance",
  "Cloud guardrail validation",
  "Executive readiness briefings"
];

const launchSteps = [
  {
    label: "Step 01",
    title: "Map the mission",
    copy: "Identify the workflow, data sensitivity, stakeholders, and operating constraints."
  },
  {
    label: "Step 02",
    title: "Define authority",
    copy: "Turn policy, review, approval, and escalation expectations into system boundaries."
  },
  {
    label: "Step 03",
    title: "Build the path",
    copy: "Stand up the environment, automation, evidence model, and product workflow."
  },
  {
    label: "Step 04",
    title: "Operate with proof",
    copy: "Run the workflow with evidence, remediation, observability, and executive visibility."
  }
];

const latestSignals = [
  {
    category: "Research",
    title: "Conscious Security for AI-speed operations",
    copy: "Why regulated teams need policy-bound execution instead of disconnected review queues."
  },
  {
    category: "Operations",
    title: "CMMC evidence as an operating system",
    copy: "How RapidFactory and KyberDocs keep proof closer to the systems and work it represents."
  },
  {
    category: "Platform",
    title: "From authority to action",
    copy: "How ODAN and Mawforge extend Holocron from governance visibility into scalable engineering."
  }
];

export function HomePage() {
  useDocumentMeta({
    canonicalPath: "/",
    description:
      "Holocron Security helps regulated organizations implement secure AI, build compliant environments, and operate with machine-speed, policy-bound defense.",
    title: "Holocron Security | Conscious Security"
  });

  return (
    <>
      <section className="home-hero trase-hero" aria-labelledby="home-hero-title">
        <HeroHolocronAnimation />
        <div className="home-hero-content trase-hero-content">
          <div className="hero-signal-strip" aria-label="Holocron operating signals">
            {heroSignals.map((signal) => (
              <span key={signal}>{signal}</span>
            ))}
          </div>

          <div className="trase-hero-grid">
            <div className="trase-hero-copy">
              <p className="section-kicker">Governed security for AI-era operations</p>
              <h1 id="home-hero-title" className="hero-title hero-title--home">
                Conscious Security.
              </h1>
              <p className="hero-statement">Security that moves at mission speed.</p>
              <p className="hero-copy">
                Holocron helps regulated teams implement AI, build CMMC-ready environments, and keep proof,
                remediation, and authority connected across every workflow.
              </p>
              <div className="hero-actions">
                <SmartLink className="button primary" href={contactHref}>
                  Request a Demo
                </SmartLink>
                <SmartLink className="button secondary" href="/solutions">
                  Explore Solutions
                </SmartLink>
              </div>
            </div>

            <aside className="hero-command-panel" aria-label="Holocron platform posture">
              <div className="command-panel-header">
                <span>Holocron Control Surface</span>
                <strong>Live posture model</strong>
              </div>
              <div className="command-status-grid" aria-hidden="true">
                <span>AI Guardrails</span>
                <span>CMMC Baseline</span>
                <span>Evidence Chain</span>
              </div>
              <div className="command-radar">
                <span />
                <span />
                <span />
                <i />
              </div>
              <div className="command-telemetry" aria-hidden="true">
                <span style={{ "--bar-value": "86%" } as CSSProperties} />
                <span style={{ "--bar-value": "74%" } as CSSProperties} />
                <span style={{ "--bar-value": "91%" } as CSSProperties} />
              </div>
              <div className="command-panel-list">
                <p>
                  <span>Authority</span>
                  Policy-bound
                </p>
                <p>
                  <span>Environment</span>
                  CMMC-oriented
                </p>
                <p>
                  <span>Evidence</span>
                  Traceable
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="executive-assurance" aria-label="Holocron trust signals">
        <div className="section-shell executive-assurance-inner">
          <p>Built for teams expected to prove control before trust is granted.</p>
          <div>
            {assuranceSignals.map((signal) => (
              <span key={signal}>{signal}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark story-section">
        <div className="section-shell story-grid">
          <div>
            <p className="section-kicker">Built on fundamentals</p>
            <h2 className="section-title">AI security only works when governance, infrastructure, and evidence move together.</h2>
          </div>
          <div className="story-copy">
            <p>
              The old model separates strategy, compliance, tooling, and operations. Holocron brings them into one
              practical path so organizations can adopt AI without surrendering control of risk, proof, or execution.
            </p>
            <SmartLink className="inline-link" href="/control-plane">
              See the authority architecture
            </SmartLink>
          </div>
        </div>
      </section>

      <section className="surface-light proof-section">
        <div className="section-shell">
          <div className="section-intro section-intro--center">
            <p className="section-kicker section-kicker--light">Operating proof</p>
            <h2 className="section-title section-title--light">The numbers tell the Holocron story.</h2>
          </div>
          <div className="proof-metric-grid">
            {proofMetrics.map((metric) => (
              <article key={metric.label} className="proof-metric">
                <strong>{metric.value}</strong>
                <h3>{metric.label}</h3>
                <p>{metric.copy}</p>
              </article>
            ))}
          </div>
          <p className="proof-note">Metrics describe Holocron&apos;s operating model and implementation structure.</p>
        </div>
      </section>

      <section className="surface-dark industry-section">
        <div className="section-shell industry-shell">
          <div>
            <p className="section-kicker">Key markets</p>
            <h2 className="section-title">Trusted where failure is not an option.</h2>
            <p className="section-copy">
              Holocron is built for organizations that need AI, automation, cloud infrastructure, and compliance
              evidence to survive real scrutiny.
            </p>
          </div>
          <div className="industry-grid">
            {industries.map((industry) => (
              <span key={industry}>{industry}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark platform-section" id="platform">
        <div className="section-shell">
          <div className="section-intro section-intro--center">
            <p className="section-kicker">Holocron platform</p>
            <h2 className="section-title">Every product. Every workflow. In your control.</h2>
            <p className="section-copy">
              The product suite is designed as an operating stack: implementation, environment, evidence, authority,
              and engineering capacity.
            </p>
          </div>

          <div className="platform-layer-grid">
            {platformLayers.map((product) => (
              <article key={product.id} className="platform-layer-card">
                <div className="layer-visual" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="icon-badge icon-badge--glass">
                  <Icon name={product.icon} />
                </div>
                <p className="layer-label">{product.id.replace("-", " ")}</p>
                <h3>{product.title}</h3>
                <p>{product.summary}</p>
                <SmartLink className="inline-link" href={product.route}>
                  Explore {product.title}
                </SmartLink>
              </article>
            ))}
          </div>
          <p className="proof-note">Metrics describe Holocron&apos;s operating model and implementation structure.</p>
        </div>
      </section>

      <ProductScreenshotShowcase
        copy="Screenshots from the product surfaces behind Holocron's compliant environment and evidence workflows."
        eyebrow="Product views"
        title="The platform is visible, usable, and built for operators."
      />

      <section className="surface-purple-glow workflow-catalog">
        <div className="section-shell workflow-catalog-shell">
          <div>
            <p className="section-kicker">Live work catalog</p>
            <h2 className="section-title">Real workflows, governed from the beginning.</h2>
          </div>
          <div className="workflow-marquee" aria-label="Representative Holocron workflows">
            <div>
              {[...workflowSignals, ...workflowSignals].map((signal, index) => (
                <span key={`${signal}-${index}`}>{signal}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="surface-light launch-section">
        <div className="section-shell">
          <div className="section-intro section-intro--center">
            <p className="section-kicker section-kicker--light">From decision to deployment</p>
            <h2 className="section-title section-title--light">Start with a controlled implementation path.</h2>
          </div>
          <div className="launch-step-grid">
            {launchSteps.map((step) => (
              <article key={step.label} className="launch-step-card">
                <span>{step.label}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark latest-section">
        <div className="section-shell">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">Media & insights</p>
              <h2 className="section-title">The latest from Holocron.</h2>
            </div>
            <SmartLink className="button secondary" href="/news">
              View Insights
            </SmartLink>
          </div>
          <div className="latest-grid">
            {latestSignals.map((signal) => (
              <article key={signal.title} className="latest-card">
                <p>{signal.category}</p>
                <h3>{signal.title}</h3>
                <span>{signal.copy}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark cta-band cta-band--home">
        <div className="section-shell section-shell--compact cta-center">
          <p className="section-kicker">Start with the right foundation</p>
          <h2 className="section-title section-title--center">
            Build secure AI, compliant environments, and evidence-backed operations without losing control.
          </h2>
          <div className="hero-actions hero-actions--centered">
            <SmartLink className="button primary" href={contactHref}>
              Get in Touch
            </SmartLink>
            <SmartLink className="button secondary" href="/solutions">
              Explore the Platform
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}
